import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Scene } from '@babylonjs/core/scene';

import { MIN_OUTLINE_WIDTH } from '../pixelate';
import { rotateLocal } from '../zone/town';

/**
 * The boxes the town is furnished with.
 *
 * Shaped like `models/armour.ts`: the **dimensions are a plain value**, so what gets
 * built and how big it is can be asserted without a scene, and only the builder at
 * the bottom touches Babylon. Where a thing stands is `game/zone/town.ts`'s job; this
 * file is only what it is made of.
 *
 * Everything is an axis-aligned box in a local frame whose **`+z` is the front** —
 * the way a shopkeeper looks, the way a door opens, the direction a stall's counter
 * faces. One yaw from `town.ts` then turns a whole assembly, so there is no rotation
 * anywhere else in the file and every bounding box a test measures is exact.
 *
 * Three rules bind everything here:
 *
 * - **Nothing drawn is pickable.** These sit in the lower middle of an isometric
 *   view, which is where the player clicks to walk; a collider on a shop sign would
 *   swallow the order that reaches it. They are merged through `ZoneRenderer`'s own
 *   `mergeInto`, which sets `isPickable = false` on every merged mesh.
 * - **Nothing is thinner than `MIN_OUTLINE_WIDTH`.** One world unit is about 25
 *   device pixels and the pixelate post-process renders at 1/`PIXEL_SIZE`, so a
 *   detail below ~0.12 units is averaged away and nothing reports it. Every
 *   dimension in every table below clears it, and `scenery.test.ts` walks the tables
 *   to prove it.
 * - **Nothing here is collision.** A station is a *position*, not a tile kind, so the
 *   server does not know a counter is there and a player can walk through one. The
 *   answer is placement, not solidity: every assembly is pushed against the house it
 *   belongs to and out of the road.
 */

/** Which material an individual box is made of. */
/**
 * `face` is separate from `skin` **because a face is a painting and skin is a colour.**
 *
 * They shared a group until 2026-08-13, so the face cell was the material for every mesh in it —
 * and that group is the head box *and both hands*. A cube takes one material on all six sides, so
 * a shopkeeper had a portrait on the back and sides of their head and a second and third one on
 * the ends of their arms. Reported as *the face textures for shop vendors are applied for every
 * face of the head's cube instead of just the front, which makes it very weird to look at.*
 */
export type SceneryGroup = 'stone' | 'timber' | 'cloth' | 'skin' | 'face' | 'glow' | 'accent';

/** One box, in its assembly's local frame. */
export interface Part {
  readonly name: string;
  /** `[width, height, depth]`, in world units. */
  readonly size: readonly [number, number, number];
  /** The box's centre, `[x, y, z]`, with `+z` forward and `y = 0` on the floor. */
  readonly at: readonly [number, number, number];
  readonly group: SceneryGroup;
}

/** Where an assembly stands and which way it looks. */
export interface Placement {
  origin: { x: number; y: number; z: number };
  yaw: number;
}

/** The meshes an assembly produced, grouped by material. */
export type SceneryParts = Record<SceneryGroup, Mesh[]>;

export function emptyParts(): SceneryParts {
  return { stone: [], timber: [], cloth: [], skin: [], face: [], glow: [], accent: [] };
}

/**
 * A workshop's stall, on the station's own street tile with `+z` facing the road.
 *
 * The whole assembly lives in the **back half** of the tile, from `z = -3.9` to
 * `z = -1.0` of an 8-unit cell whose neighbouring house presents its face at
 * `z = -4.9`. That is what leaves nearly four units of clear street in front of it,
 * which matters because scenery is not collision: the only thing stopping a player
 * walking through the counter is that the counter is not where they walk.
 *
 * **There is no awning, and that is the finding this shipped with.** The first
 * version had one — the obvious signifier of a market stall — and at this camera it
 * hid the whole shop. The camera sits 45° above the horizontal, so a horizontal slab
 * at height `H` covers everything within `H` of it measured toward the viewer: a
 * canopy at 2.78 hides nearly four units of ground behind itself, which is the
 * counter, the stock and the shopkeeper. Three of the five shops face away from the
 * camera and lost all three. **A stall at a 45° camera reads through its front, not
 * its roof**, so every coloured surface here is *vertical*: a banner between the
 * posts, a sign above them, a cloth along the counter's face. Nothing horizontal goes
 * above waist height anywhere in this file.
 */
export const SHOPFRONT: readonly Part[] = [
  // The counter, with the shop's colour on the face a customer stands at — the
  // biggest single patch of it, at eye level, and never behind anything.
  { name: 'counter', size: [3.4, 1.05, 1.1], at: [0, 0.525, -2.2], group: 'timber' },
  { name: 'counter-cloth', size: [3.5, 0.92, 0.12], at: [0, 0.5, -1.7], group: 'accent' },
  { name: 'counter-top', size: [3.7, 0.18, 1.35], at: [0, 1.14, -2.2], group: 'accent' },

  // Two posts, and the banner they carry.
  { name: 'post-left', size: [0.24, 2.7, 0.24], at: [-1.7, 1.35, -1.1], group: 'timber' },
  { name: 'post-right', size: [0.24, 2.7, 0.24], at: [1.7, 1.35, -1.1], group: 'timber' },
  { name: 'fascia', size: [4.2, 1.0, 0.24], at: [0, 2.15, -1.1], group: 'accent' },

  // The sign sits on the post tops, above the banner.
  { name: 'sign', size: [2.2, 0.8, 0.28], at: [0, 3.1, -1.1], group: 'accent' },

  // A lantern on each post, so a shop is a point of light at the end of a street.
  { name: 'lantern-left', size: [0.34, 0.4, 0.34], at: [-1.7, 2.9, -1.1], group: 'glow' },
  { name: 'lantern-right', size: [0.34, 0.4, 0.34], at: [1.7, 2.9, -1.1], group: 'glow' },

  // Stock on the counter, in the shop's colour: three lumps is all it takes to stop
  // a counter reading as a bench.
  { name: 'goods-a', size: [0.46, 0.46, 0.46], at: [-1.05, 1.46, -2.2], group: 'accent' },
  { name: 'goods-b', size: [0.54, 0.62, 0.5], at: [0.1, 1.54, -2.25], group: 'accent' },
  { name: 'goods-c', size: [0.4, 0.4, 0.4], at: [1.1, 1.43, -2.15], group: 'accent' },

  // A crate and a barrel flanking the stall, tucked against the house.
  { name: 'crate', size: [0.9, 0.9, 0.9], at: [-2.4, 0.45, -3.4], group: 'timber' },
  { name: 'barrel', size: [0.72, 1.05, 0.72], at: [2.35, 0.525, -3.45], group: 'timber' },
];

/**
 * The shopkeeper, standing at their stall.
 *
 * **Scenery, not an entity.** They are not in the snapshot, have no id, are not
 * simulated and cannot be targeted — the first non-hostile figure in the game is a
 * pile of eight boxes drawn from the layout, which costs one merged mesh for all
 * five of them. Phase 13's vendors are what would make them an entity; until
 * something needs to interact with one, an entity would be a per-tick cost and a
 * protocol change buying a figure that stands still.
 *
 * Built to 1.84 units, within a whisker of the 1.8-unit player: at roughly 30 × 45
 * device pixels a townsperson has to be the same size as a character or they read as
 * a child or a statue. The apron is the shop's accent colour, so the figure and the
 * banner beside them are visibly the same business.
 */
export const ARTISAN: readonly Part[] = [
  { name: 'legs', size: [0.64, 0.86, 0.46], at: [0, 0.43, 0], group: 'cloth' },
  { name: 'torso', size: [0.78, 0.62, 0.44], at: [0, 1.17, 0], group: 'cloth' },
  { name: 'apron', size: [0.54, 0.74, 0.14], at: [0, 1.03, 0.27], group: 'accent' },
  { name: 'head', size: [0.36, 0.36, 0.34], at: [0, 1.66, 0], group: 'skin' },
  /**
   * The face, as a plate on the front of the head — the shape the player's `cru-face` was until
   * Phase 41 painted the player's onto the skull sphere itself; a vendor's cube head keeps the plate,
   * and for the same reason: **a box wears one material on all six sides.** Painting the portrait
   * onto the head itself put a face on the back of every shopkeeper's skull.
   *
   * **0.12 deep because that is `MIN_OUTLINE_WIDTH`**, not because a face needs the thickness.
   * It was 0.04 — a plate, which is what it looks like — and `scenery.test.ts` refused it: a
   * world-space dimension below one output pixel is averaged away by the pixelate post-process
   * with nothing reporting it, which is the fault that made the hover outline invisible while
   * every line setting it was correct. The player's own `cru-face` is 0.06 and escapes only
   * because it is not in `SCENERY_TABLES`.
   *
   * Sunk to sit proud by 0.03 (`z` 0.14 ± 0.06 against the head's front at 0.17), which keeps it
   * clear of the head's own surface — two co-planar faces z-fight, and the flicker reads as a
   * texture problem rather than a geometry one.
   *
   * **Not tilted**, where the player's is. `Part` carries no rotation, and adding one would touch
   * the assembly, the merge and the bounding-box measure for a figure that stands still and is
   * looked at from one fixed angle. A vertical plate loses about a third of its area to
   * foreshortening at the 45° camera: a worse face than the player's, and a far better one than a
   * portrait wrapped around a cube.
   */
  { name: 'face', size: [0.24, 0.2, 0.12], at: [0, 1.68, 0.14], group: 'face' },
  { name: 'arm-left', size: [0.2, 0.54, 0.2], at: [-0.48, 1.18, 0.06], group: 'cloth' },
  { name: 'arm-right', size: [0.2, 0.54, 0.2], at: [0.48, 1.18, 0.06], group: 'cloth' },
  { name: 'hand-left', size: [0.18, 0.18, 0.18], at: [-0.48, 0.86, 0.1], group: 'skin' },
  { name: 'hand-right', size: [0.18, 0.18, 0.18], at: [0.48, 0.86, 0.1], group: 'skin' },
];

/**
 * Where the shopkeeper stands, in their stall's local frame.
 *
 * **At the end of the counter, not behind it**, and that is the second thing this
 * shipped with rather than the first. Behind the counter is where a shopkeeper
 * belongs and it is exactly where this camera cannot see one: at 45° elevation a
 * figure 1.35 units further from the viewer than the stall projects onto the same
 * screen rows as the stall's own banner, so the counter covered them to the chest and
 * the banner covered the rest. Nothing about the *depth* was wrong — the isolated
 * render showed a perfectly good figure standing in the right place — which is
 * precisely why an isometric scene has to be looked at rather than reasoned about.
 *
 * Beside the stall they are clear of every part of it at every facing, and a trader
 * standing at the corner of their own stall is no less a trader.
 */
export const ARTISAN_STAND: readonly [number, number, number] = [2.75, 0, -1.5];

/**
 * A house face with the door on it: `+z` is outward and `z = 0` is the block's face.
 *
 * Every part straddles that plane — sunk a little into the block and protruding a
 * little into the street — so no two surfaces are ever coplanar. That is the whole
 * discipline of this table: **a building already draws its own block**, and the last
 * time two pieces of geometry shared a plane in this town it showed as a glowing kerb
 * along the foot of every house.
 */
export const DOOR_FACE: readonly Part[] = [
  { name: 'door', size: [1.7, 2.1, 0.36], at: [0, 1.05, 0.1], group: 'timber' },
  { name: 'lintel', size: [2.1, 0.22, 0.42], at: [0, 2.22, 0.12], group: 'timber' },
  { name: 'step', size: [2.1, 0.18, 0.55], at: [0, 0.09, 0.32], group: 'timber' },
  // A lamp over the door. Twenty of these are most of what makes the town warm.
  { name: 'door-lamp', size: [0.3, 0.34, 0.3], at: [0, 2.62, 0.2], group: 'glow' },
  { name: 'window-left', size: [0.9, 0.8, 0.24], at: [-2.05, 2.35, 0.1], group: 'glow' },
  { name: 'window-right', size: [0.9, 0.8, 0.24], at: [2.05, 2.35, 0.1], group: 'glow' },
];

/** A house face with no door: two storeys of lit windows and a timber course. */
export const PLAIN_FACE: readonly Part[] = [
  { name: 'beam', size: [5.8, 0.24, 0.2], at: [0, 1.95, 0.08], group: 'timber' },
  { name: 'window-low-left', size: [0.9, 0.8, 0.24], at: [-1.4, 1.3, 0.1], group: 'glow' },
  { name: 'window-low-right', size: [0.9, 0.8, 0.24], at: [1.4, 1.3, 0.1], group: 'glow' },
  { name: 'window-high-left', size: [0.9, 0.8, 0.24], at: [-1.4, 2.55, 0.1], group: 'glow' },
  { name: 'window-high-right', size: [0.9, 0.8, 0.24], at: [1.4, 2.55, 0.1], group: 'glow' },
];

/** A street lamp, origin at its foot. */
export const LAMP_POST: readonly Part[] = [
  { name: 'base', size: [0.6, 0.3, 0.6], at: [0, 0.15, 0], group: 'stone' },
  { name: 'shaft', size: [0.26, 2.9, 0.26], at: [0, 1.6, 0], group: 'timber' },
  { name: 'head', size: [0.44, 0.52, 0.44], at: [0, 3.28, 0], group: 'glow' },
  { name: 'cap', size: [0.62, 0.18, 0.62], at: [0, 3.63, 0], group: 'stone' },
];

/** A chimney, origin on the roof slab it stands on. */
export const CHIMNEY: readonly Part[] = [
  { name: 'stack', size: [0.8, 1.2, 0.8], at: [0, 0.6, 0], group: 'stone' },
  { name: 'crown', size: [0.98, 0.2, 0.98], at: [0, 1.3, 0], group: 'stone' },
];

/** One block of the battlements, origin on the rampart's top face. */
export const MERLON: readonly Part[] = [
  { name: 'merlon', size: [2.6, 0.9, 2.6], at: [0, 0.45, 0], group: 'stone' },
];

/** Every table, for the tests that walk all of them. */
export const SCENERY_TABLES: Readonly<Record<string, readonly Part[]>> = {
  SHOPFRONT,
  ARTISAN,
  DOOR_FACE,
  PLAIN_FACE,
  LAMP_POST,
  CHIMNEY,
  MERLON,
};

/** The thinnest dimension anywhere in a table — must clear `MIN_OUTLINE_WIDTH`. */
export function thinnestDimension(parts: readonly Part[]): number {
  return parts.reduce((min, part) => Math.min(min, ...part.size), Infinity);
}

/** True when every box in every table survives the pixelate downsample. */
export function everyPartSurvivesTheDownsample(): boolean {
  return Object.values(SCENERY_TABLES).every(
    (table) => thinnestDimension(table) >= MIN_OUTLINE_WIDTH,
  );
}

/**
 * Builds one assembly and files each box under its material.
 *
 * The meshes are left parented to nothing and unmerged: `ZoneRenderer` collects a
 * whole town's worth and merges each group once, so twenty houses cost a handful of
 * draw calls rather than a hundred.
 */
export function placeParts(
  scene: Scene,
  parts: readonly Part[],
  placement: Placement,
  into: SceneryParts,
  prefix: string,
): void {
  for (const part of parts) {
    const box = MeshBuilder.CreateBox(
      `${prefix}-${part.name}`,
      { width: part.size[0], height: part.size[1], depth: part.size[2] },
      scene,
    );
    const [x, y, z] = rotateLocal(part.at, placement.yaw);
    box.position.set(placement.origin.x + x, placement.origin.y + y, placement.origin.z + z);
    box.rotation.y = placement.yaw;
    into[part.group].push(box);
  }
}
